import { useEffect, useRef, RefObject } from 'react';

type Handler = (event: MouseEvent | TouchEvent) => void;

interface UseHandleClickOutsideOptions {
  enabled?: boolean;
  eventTypes?: Array<'mousedown' | 'mouseup' | 'touchstart' | 'touchend'>;
  excludeRefs?: RefObject<HTMLElement>[];
  capture?: boolean;
}

/**
 * A hook that handles clicks outside of the specified element.
 *
 * @param callback Function to call when a click outside is detected
 * @param options Configuration options
 * @returns A ref to attach to the element to monitor
 */
function useHandleClickOutside<T extends HTMLElement = HTMLElement>(
  callback: Handler,
  options: UseHandleClickOutsideOptions = {}
): RefObject<T> {
  const {
    enabled = true,
    eventTypes = ['mousedown', 'touchstart'],
    excludeRefs = [],
    capture = true,
  } = options;

  const ref = useRef<T>(null);
  const callbackRef = useRef<Handler>(callback);

  // Update the callback ref when the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Get the event target safely
      const target = event.target as Node | null;
      if (!target) return;

      const el = ref.current;

      // Handle cases where ref isn't attached yet
      if (!el) return;

      // Check if the click is inside the element or any excluded elements
      const isInside = el.contains(target);
      const isInExcluded = excludeRefs.some(
        (excludeRef) =>
          excludeRef.current && excludeRef.current.contains(target)
      );

      // If the click is outside the element and not in any excluded elements, call the callback
      if (!isInside && !isInExcluded) {
        callbackRef.current(event);
      }
    };

    // Add event listeners for each specified event type
    eventTypes.forEach((eventType) => {
      document.addEventListener(eventType, handleClickOutside, { capture });
    });

    // Clean up by removing event listeners
    // eslint-disable-next-line consistent-return
    return () => {
      eventTypes.forEach((eventType) => {
        document.removeEventListener(eventType, handleClickOutside, {
          capture,
        });
      });
    };
  }, [enabled, eventTypes, excludeRefs, capture]);

  return ref;
}

export default useHandleClickOutside;
